import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useAuthentication, useFlightRooms, useGetFlightRoomById } from '@connect-x-app/hybrid-state';
import { ApiError, FlightroomDeepLinkAction, HttpStatus, ResponseErrorCodes } from '@connect-x-app/shared-domain-models';
import { hasOpenTasks } from '@connect-x-app/shared-utils';
import { useMatchFlightroomRoute } from '@connect-x-app/web-utils';

export const useFlightroomJoinDeepLinkAction = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { joinFlightRoom, selectedFlightRoom } = useFlightRooms();
  const { canJoinFlightRooms, canRequestAccess, isActiveMemberOfRoom } = useAuthentication();
  const [requestAccessRoomId, setRequestAccessRoomId] = useState<string | null>(null);

  const hasDispatchedRef = useRef(false);
  const prevRoomIdRef = useRef<string | null>(null);

  const location = useLocation();
  const action = searchParams.get('action');
  const externalRoomId = searchParams.get('externalRoomId');
  const match = useMatchFlightroomRoute(location.pathname);
  const roomId = match?.params.roomId ?? null;

  const { error, isError } = useGetFlightRoomById(action === FlightroomDeepLinkAction.JOIN ? roomId : null);
  const apiError = error as ApiError | null;
  const isForbidden =
    isError && apiError?.statusCode === HttpStatus.FORBIDDEN && apiError?.error !== ResponseErrorCodes.FLIGHT_ROOM_PENDING_JOIN;

  // Room state: either loaded into the store (200) or confirmed inaccessible (403)
  const roomInStore = selectedFlightRoom?.id === roomId;
  const roomResolved = roomInStore || isForbidden;

  useEffect(() => {
    if (action !== FlightroomDeepLinkAction.JOIN) {
      return;
    }
    if (!roomId) {
      return;
    }
    if (!roomResolved) {
      return;
    }

    if (roomId !== prevRoomIdRef.current) {
      prevRoomIdRef.current = roomId;
      hasDispatchedRef.current = false;
    }

    if (hasDispatchedRef.current) {
      return;
    }
    hasDispatchedRef.current = true;

    const consumeAction = () => {
      setSearchParams(
        params => {
          params.delete('action');
          params.delete('externalRoomId');
          return params;
        },
        { replace: true },
      );
    };

    if (roomInStore) {
      if (isActiveMemberOfRoom(selectedFlightRoom) || hasOpenTasks(selectedFlightRoom.tasks)) {
        consumeAction();
        return;
      }
    }

    if (!canJoinFlightRooms) {
      if (canRequestAccess) {
        setRequestAccessRoomId(roomId);
      }
      consumeAction();
      return;
    }

    joinFlightRoom(roomId).then(consumeAction, consumeAction);
  }, [
    action,
    roomId,
    roomResolved,
    roomInStore,
    selectedFlightRoom,
    canJoinFlightRooms,
    canRequestAccess,
    isActiveMemberOfRoom,
    joinFlightRoom,
    setSearchParams,
  ]);

  return { requestAccessExternalId: externalRoomId, requestAccessRoomId };
};
